import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Tracks which section is in view. While a menu-triggered smooth scroll is in
 * progress, the observer is paused so the active indicator doesn't flicker
 * across every section it passes.
 */
export function useActiveSection(ids: string[]) {
  const [active, setActive] = useState(ids[0] ?? '')
  const lockedRef = useRef(false)
  const unlockTimer = useRef<number>()

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (lockedRef.current) return
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id)
        })
      },
      { rootMargin: '-45% 0px -50% 0px' },
    )
    ids.forEach((id) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [ids.join(',')])

  const navigateTo = useCallback((id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    setActive(id)
    lockedRef.current = true
    window.clearTimeout(unlockTimer.current)

    const unlock = () => {
      lockedRef.current = false
      window.removeEventListener('scrollend', unlock)
    }
    window.addEventListener('scrollend', unlock, { once: true })
    // Fallback for browsers without `scrollend`
    unlockTimer.current = window.setTimeout(unlock, 1200)

    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    history.replaceState(null, '', `#${id}`)
  }, [])

  return { active, navigateTo }
}

export function useScrolled(offset = 12): boolean {
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > offset)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [offset])
  return scrolled
}
